import { css } from '@emotion/react';
import { t } from 'i18n';
import { FEE_ITEM_TYPE, FeeItem, formatCurrency, formatPoints } from 'shared';

import { SasIcon } from '../../SasIcon';
import { theme } from '../../theme';
import { Item } from '../Item';
import { LegacyCartTotal } from '../LegacyCartTotal';
import { PriceDetail } from '../PriceDetail';
import { LegacyPriceItem } from '../types';
import { createCartItem } from '../utils';

const styles = {
  totalContainer: css`
    display: flex;
    justify-content: space-between;
  `,
  summaryWrapper: css`
    display: flex;
    flex-direction: column;
  `,
  summaryContainer: css`
    display: flex;
    justify-content: space-between;
    border-radius: ${theme.borders.radius.md};
    border: 1px solid #d8d8d8;
    padding: ${theme.spacing.md};
    background-color: ${theme.colors.white};
    flex-direction: column;

    @media (max-width: ${theme.breakpoints.laptop}) {
      &:last-of-type {
        margin-top: ${theme.spacing.sm};
      }
    }
  `,

  informationContainer: css`
    margin-bottom: ${theme.spacing.sm};
    display: flex;
  `,
  divider: css`
    background-color: ${theme.colors.separator};
    width: 100%;
    margin-top: ${theme.spacing.sm};
  `,
  nonMarginBottom: css`
    margin-bottom: 0;
  `,
  container: css`
    width: 100%;
  `,
  taxesContainer: css`
    display: flex;
    justify-content: space-between;
  `,
};

type Props = {
  summaryItem: LegacyPriceItem;
  pointOfSale: string;
  shouldDisplayPrice: boolean;
  isSarRebooking: boolean;
};

export const CartSummary = ({ pointOfSale, summaryItem, shouldDisplayPrice, isSarRebooking = false }: Props) => {
  const priceItems = summaryItem.feeItems.filter(
    row => row.itemType !== FEE_ITEM_TYPE.Total && row.itemType !== FEE_ITEM_TYPE.Taxes
  );

  const taxItem = summaryItem.feeItems.find(row => row.itemType === FEE_ITEM_TYPE.Taxes);
  const taxListItem = createCartItem(taxItem);

  return (
    <div css={styles.summaryWrapper}>
      <div css={styles.summaryContainer}>
        <h4>{t('common:summary')}</h4>
        <hr css={styles.divider} />

        <div css={styles.container}>
          {shouldDisplayPrice ? (
            <>
              {priceItems.map((row, index) => renderPriceItem(row, index, pointOfSale))}
              {taxListItem && taxListItem.value !== 0 && (
                <div css={styles.taxesContainer}>
                  <PriceDetail priceListItem={taxListItem} pointOfSale={pointOfSale} />
                  <div>{formatCurrency(taxListItem.value, pointOfSale)}</div>
                </div>
              )}
              <hr css={styles.divider} />
              <div css={styles.totalContainer}>
                <h3 css={styles.nonMarginBottom}>{t('common:total')}</h3>
                <LegacyCartTotal
                  pos={pointOfSale}
                  feeItems={summaryItem.feeItems}
                  align="right"
                  small
                  isSarRebooking={isSarRebooking}
                />
              </div>
            </>
          ) : (
            <div css={styles.informationContainer}>
              <SasIcon name="cl--info" />
              <span>{t('cart.selectBounds')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const renderPriceItem = (row: FeeItem, index: number, pointOfSale: string) => {
  const { label, amount, priceType, discountedPoints } = row;

  let hasDiscount = Boolean(discountedPoints);
  if (amount === discountedPoints) {
    hasDiscount = false;
  }

  return (
    <div key={`${label}-${index}`}>
      <Item
        discountedPoints={hasDiscount ? formatPoints(discountedPoints ?? 0, pointOfSale) : undefined}
        label={label}
        price={priceType === 'points' ? formatPoints(amount, pointOfSale) : formatCurrency(amount, pointOfSale)}
      />
    </div>
  );
};
